import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { registerPushSubscriptionAs, unregisterPushSubscriptionAs } from './pushSubscriptionWriter';
import type { PushAttemptResult, PushMaintenanceResult } from './pushWeb';

/** This device's last-registered native token — the handle unregister uses. */
let currentNativeToken: string | null = null;

/** At most one native registration attempt at a time (operation latch). */
let nativeOpActive = false;

/** How long to wait for the platform's registration callback before giving
 *  up and releasing the latch (an absent callback must not wedge push). */
export const NATIVE_REGISTRATION_TIMEOUT_MS = 20_000;

/**
 * One operation-scoped native registration attempt (Phase 11 correction 2).
 *
 * The Capacitor token callback is asynchronous and used to ride a PERSISTENT
 * listener that wrote to the backend with whatever credentials were active
 * when the token finally arrived — the cross-account race. Now every attempt:
 *   • installs its own 'registration' + 'registrationError' listeners BEFORE
 *     calling register(),
 *   • scopes the callback to the captured identity/operation guard — a token
 *     arriving after logout, an account switch, or invalidation causes ZERO
 *     API calls (and the backend write itself goes through the identity-
 *     guarded seam, closing the credential-acquisition window too),
 *   • removes its listeners on success, registration error, and timeout,
 *   • releases the latch in every outcome so a later attempt can run.
 *
 * Notification-received / action listeners are not touched here — this file
 * never owned any; only registration listeners are managed.
 */
async function runNativeRegistration(identityKey: string, stillValid?: () => boolean): Promise<PushMaintenanceResult> {
  if (nativeOpActive) return { status: 'failed' };
  nativeOpActive = true;

  try {
    return await new Promise<PushMaintenanceResult>((resolve) => {
      let settled = false;
      const handles: Array<{ remove: () => Promise<void> }> = [];
      let timer: ReturnType<typeof setTimeout> | null = null;

      const finish = (result: PushMaintenanceResult) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        for (const handle of handles.splice(0)) void handle.remove().catch(() => {});
        resolve(result);
      };

      const install = async () => {
        handles.push(await PushNotifications.addListener('registration', (token) => {
          void (async () => {
            if (stillValid && !stillValid()) return finish({ status: 'skipped', reason: 'stale' });
            const rotated = currentNativeToken !== null && currentNativeToken !== token.value;
            currentNativeToken = token.value;
            const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
            const written = await registerPushSubscriptionAs(identityKey, token.value, platform, stillValid);
            if (written === 'stale') return finish({ status: 'skipped', reason: 'stale' });
            finish(written === 'registered' ? { status: 'registered', rotated } : { status: 'failed' });
          })();
        }));
        handles.push(await PushNotifications.addListener('registrationError', () => {
          finish({ status: 'failed' });
        }));
      };

      timer = setTimeout(() => finish({ status: 'failed' }), NATIVE_REGISTRATION_TIMEOUT_MS);

      void install()
        .then(() => PushNotifications.register())
        .catch(() => finish({ status: 'failed' }));
    });
  } finally {
    nativeOpActive = false;
  }
}

/** READ-ONLY permission inspection — checkPermissions never prompts. */
export async function getNativePushPermission(): Promise<'granted' | 'denied' | 'default' | 'unsupported'> {
  if (!Capacitor.isNativePlatform()) return 'unsupported';
  const current = await PushNotifications.checkPermissions();
  if (current.receive === 'granted') return 'granted';
  if (current.receive === 'denied') return 'denied';
  return 'default';
}

/**
 * The explicit ENABLE pipeline for native — same contract as web: call ONLY
 * from a user gesture; prompting happens here and nowhere else.
 */
export async function registerNativePush(identityKey: string, stillValid?: () => boolean): Promise<PushAttemptResult> {
  if (!Capacitor.isNativePlatform()) return { ok: false, reason: 'unsupported' };

  try {
    const current = await PushNotifications.checkPermissions();
    let granted = current.receive === 'granted';
    if (!granted && current.receive !== 'denied') {
      const requested = await PushNotifications.requestPermissions();
      granted = requested.receive === 'granted';
    }
    if (!granted) {
      return { ok: false, reason: current.receive === 'denied' ? 'blocked' : 'dismissed' };
    }

    const result = await runNativeRegistration(identityKey, stillValid);
    if (result.status === 'registered') return { ok: true };
    if (result.status === 'skipped' && result.reason === 'stale') return { ok: false, reason: 'stale' };
    return { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

/**
 * Silent startup maintenance — granted-only (Phase 11 correction).
 * Structurally unable to prompt: only the read-only checkPermissions() is
 * called here, never requestPermissions(). register() on an already-granted
 * device fetches/refreshes the FCM token without any OS dialog.
 */
export async function maintainNativePushRegistration(identityKey: string, stillValid?: () => boolean): Promise<PushMaintenanceResult> {
  if (!Capacitor.isNativePlatform()) return { status: 'skipped', reason: 'unsupported' };
  try {
    const current = await PushNotifications.checkPermissions();
    if (current.receive !== 'granted') return { status: 'skipped', reason: 'permission' };
    return await runNativeRegistration(identityKey, stillValid);
  } catch {
    return { status: 'failed' };
  }
}

/** DISABLE pipeline for native: delete this device's backend subscription
 *  row (best effort — the account-level push_enabled=false already
 *  suppresses delivery). Identity-bound: a stale operation (the initiating
 *  account switched away) skips the DELETE silently instead of sending it
 *  under the new account's credentials. Never prompts. */
export async function unregisterNativePush(identityKey: string, stillValid?: () => boolean): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true;
  try {
    if (!currentNativeToken) return true;
    const deleted = await unregisterPushSubscriptionAs(identityKey, currentNativeToken, stillValid);
    if (deleted === 'stale') return true;   // silent skip — never delete under another account
    if (deleted === 'failed') return false;
    currentNativeToken = null;
    return true;
  } catch {
    return false;
  }
}
